import React, { useState, useCallback } from 'react';
import { HELP_ABOUT } from '../content/helpAbout.js';
import { HELP_FAQ_SECTIONS } from '../content/helpFaq.js';
import { splitFaqInlineLinks } from '../content/parseUserFaq.js';
import { resolveHelpVideoUrl, resolveYouTubeEmbedUrl } from '../utils/helpVideo.js';
import ContactSupportPanel from './ContactSupportPanel.jsx';

const HELP_SECTIONS = [
  {
    id: 'about',
    title: 'What are we',
    titleClass: 'text-xl md:text-2xl',
    subtitle: 'About Shingami & vision',
    icon: (
      <svg className="w-7 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.75}
          d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
    ),
  },
  {
    id: 'walkthrough',
    title: 'Setting up the Account',
    titleClass: 'text-xl md:text-2xl',
    subtitle: 'Setup video guide',
    icon: (
      <svg className="w-7 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.75}
          d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
        />
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.75}
          d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
    ),
  },
  {
    id: 'faq',
    title: 'FAQ',
    titleClass: 'text-xl md:text-2xl',
    subtitle: 'Common questions',
    icon: (
      <svg className="w-7 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.75}
          d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9 5.25h.008v.008H12v-.008z"
        />
      </svg>
    ),
  },
  {
    id: 'contact',
    title: 'Contact support',
    titleClass: 'text-xl md:text-2xl',
    subtitle: 'Questions or urgent help',
    icon: (
      <svg className="w-7 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.75}
          d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
        />
      </svg>
    ),
  },
];

const DEFAULT_FAQ_CATEGORY_ID = HELP_FAQ_SECTIONS[0]?.id ?? null;

/** Fixed layout — topic cards and opened panel share the same shell width/height. */
const TOPIC_ICON_CLASS = 'bg-blue-600 text-white';
const TOPIC_CARD_SIZE_CLASS = 'w-[280px] h-[180px] flex-shrink-0';
const HELP_SHELL_WIDTH_CLASS = 'w-[75%] min-w-[280px] max-w-[1200px]';
const HELP_PANEL_HEIGHT_CLASS = 'h-[560px]';

function Chevron({ open }) {
  return (
    <span
      className={`flex-shrink-0 text-gray-400 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
      aria-hidden
    >
      ▾
    </span>
  );
}

function TopicCard({ section, onSelect }) {
  return (
    <button
      type="button"
      onClick={() => onSelect(section.id)}
      className={`flex flex-col items-start justify-between text-left rounded-2xl bg-white p-6 box-border shadow-xl hover:shadow-2xl transition-shadow ${TOPIC_CARD_SIZE_CLASS}`}
    >
      <span
        className={`inline-flex items-center justify-center w-12 h-12 rounded-xl mb-4 ${TOPIC_ICON_CLASS}`}
      >
        {section.icon}
      </span>
      <div className="min-w-0 w-full">
        <span
          className={`block font-semibold text-gray-900 leading-snug line-clamp-2 ${section.titleClass || 'text-lg'}`}
        >
          {section.title}
        </span>
        <span className="block text-sm text-gray-500 mt-1.5 leading-snug line-clamp-2">{section.subtitle}</span>
      </div>
    </button>
  );
}

function TopicCardPicker({ onSelect }) {
  return (
    <div
      className={`flex flex-col items-center justify-center w-full ${HELP_PANEL_HEIGHT_CLASS}`}
    >
      <div className="flex flex-wrap justify-center gap-3 md:gap-4 max-w-full">
        {HELP_SECTIONS.map((section) => (
          <TopicCard key={section.id} section={section} onSelect={onSelect} />
        ))}
      </div>
    </div>
  );
}

function FaqInlineText({ text }) {
  const parts = splitFaqInlineLinks(text);
  return (
    <>
      {parts.map((part, index) =>
        part.type === 'link' ? (
          <a
            key={`${part.href}-${index}`}
            href={part.href}
            className="text-blue-600 underline hover:text-blue-800"
            target="_blank"
            rel="noopener noreferrer"
          >
            {part.label}
          </a>
        ) : (
          <span key={`text-${index}`}>{part.value}</span>
        )
      )}
    </>
  );
}

function FaqAnswerBlocks({ blocks }) {
  return (
    <div className="space-y-3 text-sm text-gray-700 leading-relaxed">
      {blocks.map((block, index) => {
        if (block.type === 'p') {
          return (
            <p key={`p-${index}`}>
              <FaqInlineText text={block.text} />
            </p>
          );
        }
        if (block.type === 'ul') {
          return (
            <ul key={`ul-${index}`} className="list-disc pl-5 space-y-1">
              {block.items.map((item) => (
                <li key={item.slice(0, 48)}>
                  <FaqInlineText text={item} />
                </li>
              ))}
            </ul>
          );
        }
        if (block.type === 'ol') {
          return (
            <ol key={`ol-${index}`} className="list-decimal pl-5 space-y-1">
              {block.items.map((item) => (
                <li key={item.slice(0, 48)}>
                  <FaqInlineText text={item} />
                </li>
              ))}
            </ol>
          );
        }
        return null;
      })}
    </div>
  );
}

function FaqAnswer({ item }) {
  if (item.answerBlocks?.length) {
    return <FaqAnswerBlocks blocks={item.answerBlocks} />;
  }
  if (Array.isArray(item.answer)) {
    return (
      <ul className="list-disc pl-5 space-y-2 text-sm text-gray-700 leading-relaxed">
        {item.answer.map((paragraph) => (
          <li key={paragraph.slice(0, 40)}>{paragraph}</li>
        ))}
      </ul>
    );
  }
  return <p className="text-sm text-gray-700 leading-relaxed">{item.answer}</p>;
}

function FaqQuestionRow({ item, isOpen, onToggle }) {
  const panelId = `faq-panel-${item.id}`;
  const buttonId = `faq-button-${item.id}`;

  return (
    <div className="border-b border-gray-200 last:border-b-0">
      <button
        type="button"
        id={buttonId}
        aria-expanded={isOpen}
        aria-controls={panelId}
        onClick={onToggle}
        className="w-full flex items-start justify-between gap-4 py-4 text-left hover:bg-gray-50/80 min-h-[44px] px-1"
      >
        <span className="text-sm md:text-base font-medium text-gray-900 pr-2">{item.question}</span>
        <Chevron open={isOpen} />
      </button>
      {isOpen && (
        <div id={panelId} role="region" aria-labelledby={buttonId} className="pb-5 px-1">
          <FaqAnswer item={item} />
        </div>
      )}
    </div>
  );
}

function AboutPanel() {
  return (
    <div className="space-y-5 text-sm text-gray-700 leading-relaxed">
      <p>{HELP_ABOUT.body}</p>
      <div>
        <h4 className="text-sm font-semibold text-gray-800 mb-2">{HELP_ABOUT.visionTitle}</h4>
        <p className="mb-2">{HELP_ABOUT.visionBody}</p>
        <p className="text-gray-600">{HELP_ABOUT.visionDetail}</p>
      </div>
    </div>
  );
}

function WalkthroughPanel({ embedUrl }) {
  return embedUrl ? (
    <div className="w-full h-[360px] rounded-xl overflow-hidden border border-gray-200 bg-gray-100">
      <iframe
        title="Setting up the Account"
        src={embedUrl}
        className="w-full h-full"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        allowFullScreen
      />
    </div>
  ) : (
    <div className="w-full h-[360px] flex items-center justify-center rounded-xl border border-dashed border-gray-300 bg-gray-50 px-6 text-sm text-gray-600 text-center">
      Setup video is not configured yet.
    </div>
  );
}

function FaqSidebarPanel({ selectedCategoryId, openFaqId, onSelectCategory, onToggleFaq }) {
  const activeCategory =
    HELP_FAQ_SECTIONS.find((section) => section.id === selectedCategoryId) ?? HELP_FAQ_SECTIONS[0];

  if (!activeCategory) {
    return null;
  }

  return (
    <div className="flex flex-col md:flex-row h-full min-h-0">
      <nav
        aria-label="FAQ categories"
        className="md:w-56 lg:w-64 flex-shrink-0 border-b md:border-b-0 md:border-r border-gray-200 bg-gray-50/60 md:bg-white py-2 md:overflow-y-auto"
      >
        <p className="hidden md:block px-4 pt-2 pb-3 text-xs font-semibold uppercase tracking-wide text-gray-400">
          Topics
        </p>
        <ul className="flex md:flex-col gap-0 overflow-x-auto md:overflow-visible">
          {HELP_FAQ_SECTIONS.map((section) => {
            const isActive = section.id === activeCategory.id;
            return (
              <li key={section.id} className="flex-shrink-0 md:flex-shrink">
                <button
                  type="button"
                  onClick={() => onSelectCategory(section.id)}
                  aria-current={isActive ? 'page' : undefined}
                  className={`w-full text-left px-4 py-2.5 md:py-3 text-sm whitespace-nowrap md:whitespace-normal transition-colors border-l-0 md:border-l-2 ${
                    isActive
                      ? 'md:border-blue-600 bg-white md:bg-blue-50/40 text-gray-900 font-medium'
                      : 'md:border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-100/80'
                  }`}
                >
                  {section.title}
                </button>
              </li>
            );
          })}
        </ul>
      </nav>

      <div className="flex-1 p-5 md:p-8 min-w-0 overflow-y-auto">
        <nav aria-label="Breadcrumb" className="text-xs text-gray-500 mb-4">
          <span>Help &amp; Support</span>
          <span className="mx-1.5" aria-hidden>
            ›
          </span>
          <span className="text-gray-700">{activeCategory.title}</span>
        </nav>

        <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-6">{activeCategory.title}</h3>

        <div className="divide-y divide-gray-200 border-t border-gray-200">
          {activeCategory.items.map((item) => (
            <FaqQuestionRow
              key={item.id}
              item={item}
              isOpen={openFaqId === item.id}
              onToggle={() => onToggleFaq(item.id)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

export default function HelpSupportView() {
  const [activeSection, setActiveSection] = useState(null);
  const [selectedFaqCategoryId, setSelectedFaqCategoryId] = useState(DEFAULT_FAQ_CATEGORY_ID);
  const [openFaqId, setOpenFaqId] = useState(null);
  const [contactPanelKey, setContactPanelKey] = useState(0);

  const videoUrl = resolveHelpVideoUrl();
  const embedUrl = resolveYouTubeEmbedUrl(videoUrl);

  const selectSection = useCallback((sectionId) => {
    setActiveSection(sectionId);
    if (sectionId === 'faq') {
      setSelectedFaqCategoryId(DEFAULT_FAQ_CATEGORY_ID);
    }
    if (sectionId === 'contact') {
      setContactPanelKey((key) => key + 1);
    }
    setOpenFaqId(null);
  }, []);

  const clearSection = useCallback(() => {
    setActiveSection(null);
    setOpenFaqId(null);
  }, []);

  const selectFaqCategory = useCallback((categoryId) => {
    setSelectedFaqCategoryId(categoryId);
    setOpenFaqId(null);
  }, []);

  const toggleFaq = useCallback((id) => {
    setOpenFaqId((prev) => (prev === id ? null : id));
  }, []);

  const activeMeta = HELP_SECTIONS.find((s) => s.id === activeSection);
  const showingContent = Boolean(activeSection && activeMeta);

  return (
    <div className="w-full flex justify-center p-4 md:p-8">
      <div className={`${HELP_SHELL_WIDTH_CLASS} flex flex-col ${showingContent ? 'space-y-3' : ''}`}>
        {!showingContent ? (
          <TopicCardPicker onSelect={selectSection} />
        ) : (
          <>
            <button
              type="button"
              onClick={clearSection}
              className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:text-blue-800 min-h-[44px] self-start"
            >
              <span aria-hidden>←</span>
              All topics
            </button>

            <div
              id="help-active-panel"
              role="region"
              aria-label={activeMeta.title}
              className={`bg-white rounded-2xl shadow-xl overflow-hidden flex flex-col w-full ${HELP_PANEL_HEIGHT_CLASS}`}
            >
            {activeSection === 'faq' ? (
              <FaqSidebarPanel
                selectedCategoryId={selectedFaqCategoryId}
                openFaqId={openFaqId}
                onSelectCategory={selectFaqCategory}
                onToggleFaq={toggleFaq}
              />
            ) : activeSection === 'contact' ? (
              <ContactSupportPanel key={contactPanelKey} />
            ) : (
              <div className="p-6 md:p-10 flex-1 min-h-0 overflow-y-auto">
                <h3 className="text-lg font-bold text-gray-800 mb-4">{activeMeta.title}</h3>
                {activeSection === 'about' && <AboutPanel />}
                {activeSection === 'walkthrough' && (
                  <WalkthroughPanel embedUrl={embedUrl} />
                )}
              </div>
            )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
